<script setup lang="ts">
import { computed } from 'vue'
import type { ActivityRecord, Activity } from '../enums/activities'
import { micromark } from 'micromark'
import GenericExpansionIcon from './GenericExpansionIcon.vue'
import { type Account, TheTooltip } from '@wastedondestiny/destiny-library'
import LevelIcon from './LevelIcon.vue'

const props = withDefaults(defineProps<{
  account: Account,
  accounts: Account[],
  activity: Activity,
  activityType: keyof ActivityRecord,
  showAllAnyway?: boolean,
  onlyPortal?: boolean,
  noInteract?: boolean,
  noIcon?: boolean
}>(), {
  showAllAnyway: false,
  onlyPortal: false,
  noInteract: false,
  noIcon: false
})

const whoHas = computed(() =>
  props.accounts.filter(account =>
    props.activity.ownership.some(x =>
      ((account.versionsOwned ?? 0) & x) === x ||
      account.seasonsOwned?.includes(x) ||
      account.dungeonsOwned?.includes(x)
    )
  ).length
)
const everyoneHas = computed(() =>
  !props.activity.ownership.length ||
  whoHas.value === props.accounts.length
)
const someHave = computed(() =>
  !everyoneHas.value &&
  whoHas.value > 0 &&
  whoHas.value <= props.accounts.length
)
const iHave = computed(() =>
  !props.activity.ownership.length || 
  props.activity.ownership.some(x =>
    ((props.account.versionsOwned ?? 0) & x) === x ||
    props.account.seasonsOwned?.includes(x) ||
    props.account.dungeonsOwned?.includes(x)
  )
)
const enoughHave = computed(() =>
  props.activity.maxPlayers &&
  someHave.value &&
  whoHas.value >= props.activity.maxPlayers &&
  iHave.value
)
const noOneHas = computed(() =>
  !iHave.value &&
  !someHave.value
)
const rotation = computed(() => {
  if (props.activity.rotations?.length && props.activity.rotationFrequencyHrs && props.activity.rotationStartDate) {
    const timeSinceStart = Date.now() - props.activity.rotationStartDate.getTime()
    const rotationFrequencyMs = props.activity.rotationFrequencyHrs * 60 * 60 * 1000
    const rotationIndex = Math.ceil(timeSinceStart / rotationFrequencyMs) % props.activity.rotations.length
    return props.activity.rotations[rotationIndex]
  }

  return null
})
const fullName = computed(() => {
  if (rotation.value === '(featured)' || rotation.value === '(unavailable)') return props.activity.name
  return rotation.value ? `${props.activity.name} ${rotation.value}` : props.activity.name
})
const isFeatured = computed(() => {
  if (props.activity.isAlwaysFeatured) return true
  return rotation.value === '(featured)'
})
const isUnavailable = computed(() => {
  return rotation.value === '(unavailable)'
})
const description = computed(() => {
  let addonMessage = ''
  if (props.activity.isInWorld && props.activity.isInPortal) {
    addonMessage = '> **Note** This activity is selectable from the World tab, Patrol, another activity, or the Portal.'
  }
  if (props.activity.isInWorld && !props.activity.isInPortal) {
    addonMessage = '> ***Warning*** This activity is selectable only from the World tab, Patrol or another activity.'
  }
  if (props.activity.isInPortal && !props.activity.isInWorld) {
    addonMessage = '> ***Warning*** This activity is selectable only from the Portal.'
  }

  return addonMessage ? `${addonMessage}
  
${props.activity.description}` : props.activity.description
})
</script>

<template>
  <div v-bind="$attrs" class="w-full py-0.5 px-2 rounded-2xl flex justify-between items-center border-2" :class="{
    'bg-emerald-300 border-emerald-300': !isUnavailable && (everyoneHas || enoughHave),
    'bg-yellow-200 border-yellow-200': !isUnavailable && iHave && someHave && !enoughHave,
    'bg-zinc-100 border-zinc-100 text-black/50': !isUnavailable && (noOneHas || !iHave),
    'border-dashed bg-opacity-50 bg-zinc-100 border-zinc-100 text-black/50': isUnavailable
  }">
    <span class="flex w-full items-center pr-1 text-black relative">
      <TheTooltip :no-interact="noInteract" other-classes="flex w-full items-center gap-1 text-sm">
        <GenericExpansionIcon v-if="!noIcon" :expansion="activity.releasedWith" other-classes="h-4 w-4 min-w-4" />
        <span class="text-3xl leading-3 flex w-3 flex-shrink-0 text-center absolute -left-2.5 -bottom-1" v-if="activity.rotations?.length">
          <img src="../assets/refresh.svg" class="h-3 w-3 bg-zinc-300 invert rounded-full inline-block" alt="This activity rotates" />
        </span>
        <span class="text-3xl leading-3 flex w-3 flex-shrink-0 text-center" v-if="isFeatured">
          <LevelIcon other-classes="h-3 w-3 inline-block" />
        </span>
        <span class="w-full">
          {{ fullName || activity.name }}
        </span>
        <small class="bg-zinc-100/50 px-1.5 py-0.5 inline-block rounded-xl text-xs h-4 leading-none" v-if="activity.maxPlayers">{{ !activity.ownership.length ? accounts.length : whoHas }}/{{ activity.maxPlayers }}</small>
        <template #icon>
          <img v-if="activityType === 'raid'" src="../assets/raid.svg" alt="Raid" />
          <img v-if="activityType === 'dungeon'" src="../assets/dungeon.png" alt="Dungeon" />
          <img v-if="activityType === 'fireteam'" src="../assets/strike.svg" alt="Fireteam Ops" />
          <img v-if="activityType === 'core'" src="../assets/destiny.svg" alt="Core activity" />
          <img v-if="activityType === 'pinnacle'" src="../assets/engram.svg" alt="Pinnacle Ops" />
          <img v-if="activityType === 'arena'" src="../assets/quest.svg" alt="Arena Ops" />
          <img v-if="activityType === 'solo'" src="../assets/quest.svg" alt="Solo Ops" />
          <img v-if="activityType === 'expansion'" src="../assets/adventure.svg" alt="Expansion activity" />
        </template>
        <template #content>
          <span class="block">{{ fullName || activity.name }}</span>
          <span class="text-xs whitespace-nowrap">Released {{ activity.date.toLocaleDateString('en-US', { dateStyle: 'long' }) }}</span>
          <span class="markdown" v-html="micromark(description)" />
        </template>
      </TheTooltip>
    </span>
    <template v-if="!activity.ownership.length">
      <TheTooltip :no-interact="noInteract" other-classes="flex-shrink-0">
        <img src="../assets/free.svg" class="h-4 w-4 opacity-75" alt="This activity is free to play for all players" />
        <template #icon>
          <img src="../assets/free.svg" alt="This activity is free to play for all players" />
        </template>
        <template #content>This activity is free to play for all players</template>
      </TheTooltip>
    </template>
    <template v-if="activity.ownership.length">
      <template v-if="everyoneHas">
        <TheTooltip :no-interact="noInteract" other-classes="flex-shrink-0">
          <img src="../assets/check.svg" class="h-4 w-4 opacity-75" alt="Everyone has access to this activity" />
          <template #icon>
            <img src="../assets/check.svg" alt="Everyone has access to this activity" />
          </template>
          <template #content>Everyone has access to this activity</template>
        </TheTooltip>
      </template>
      <template v-else-if="enoughHave">
        <TheTooltip :no-interact="noInteract" other-classes="flex-shrink-0">
          <img src="../assets/check.svg" class="h-4 w-4 opacity-75" alt="Enough people have access to this activity" />
          <template #icon>
            <img src="../assets/check.svg" alt="Enough people have access to this activity" />
          </template>
          <template #content>Enough people have access to this activity</template>
        </TheTooltip>
      </template>
      <template v-else-if="iHave && someHave">
        <TheTooltip warning :no-interact="noInteract" other-classes="flex-shrink-0">
          <img src="../assets/warning.svg" class="h-4 w-4 opacity-75" alt="Not enough people have access to this activity" />
          <template #icon>
            <img src="../assets/warning.svg" alt="Not enough people have access to this activity" />
          </template>
          <template #content>Not enough people have access to this activity</template>
        </TheTooltip>
      </template>
      <template v-else>
        <span class="h-4 w-4 inline-block"></span>
      </template>
    </template>
  </div>
</template>
